import React from "react";
import type { ListColumn } from "../../../../../shared/frontend/list/listColumn";
import { settingsListDataIndexPath } from "../../../../../shared/frontend/settings/settingsListDataAccess";
import { SettingsListPage, type ColumnDefinition, type ProColumnsWithMeasure } from "./SettingsListPage";

/** valueType der Aktionsspalte. */
const VALUE_TYPE_OPTION = "option";

export type DrawerItemsTableSelection<T> = {
  selectedRowKeys: React.Key[];
  onChange: (selectedKeys: React.Key[], selectedRows: T[]) => void;
  hideSelectAll?: boolean;
  /** Einzelauswahl (Radio) oder Mehrfachauswahl (Checkbox, Standard) */
  type?: "radio" | "checkbox";
  /**
   * Zusätzlich zur sichtbaren Auswahlspalte togglet ein Klick irgendwo auf die Zeile die Auswahl
   * (Klicks auf die Auswahlspalte werden dabei nicht doppelt gewertet).
   */
  selectionByRowClick?: boolean;
  /** Bei selectionByRowClick: Zeilen mit disabled werden nicht auswählbar. */
  getCheckboxProps?: (record: T) => { disabled?: boolean };
};

export type DrawerItemsTableProps<T extends object> = {
  /** Daten der Liste */
  dataSource: T[];
  /** Spaltendefinitionen */
  columns: ListColumn<T>[];
  /** Zeilenschlüssel (z. B. "id") */
  rowKey: keyof T | string;
  /** Optionale Zeilenauswahl. Wenn nicht gesetzt, reine Anzeige. */
  rowSelection?: DrawerItemsTableSelection<T>;
  /**
   * Zeilenklick ohne Auswahlspalte — eine Position weiterführen, nicht mehrere anhaken.
   * Nicht zusammen mit `rowSelection` verwenden.
   */
  onRowClick?: (record: T) => void;
  /** Hebt die gerade bearbeitete Zeile hervor, wenn `onRowClick` gesetzt ist. */
  isRowActive?: (record: T) => boolean;
  /** Sperrt den Zeilenklick (z. B. schon gebuchte Empfangsposition). */
  isRowDisabled?: (record: T) => boolean;
  /** Maximale Höhe des Scroll-Bereichs (px) */
  scrollY?: number;
  /** @deprecated Wirkungslos: die Zeilenliste hat keinen waagerechten Scrollbalken. */
  scrollX?: number | string;
  /** Text, wenn dataSource leer ist (statt Liste) */
  emptyText?: React.ReactNode;
};

function columnKeyOf<T>(column: ListColumn<T>, index: number): string {
  return String(column.key ?? (settingsListDataIndexPath(column.dataIndex) || `column-${index}`));
}

/**
 * Einheitliche Positionsliste in Drawern (Zahlung, Stornierung, Empfang) über die gemeinsame Listenseite
 * (eingebettet, ohne Spaltenauswahl und Seitenwechsel). Optional mit Zeilenauswahl oder mit Zeilenklick
 * ohne Auswahlspalte (`onRowClick`); Sortierung über `sorter` der Spalten wie in jeder Liste.
 */
export function DrawerItemsTable<T extends object>({
  dataSource,
  columns,
  rowKey,
  rowSelection,
  onRowClick,
  isRowActive,
  isRowDisabled,
  scrollY = 300,
  emptyText
}: DrawerItemsTableProps<T>): React.ReactElement {
  const rowKeyStr = rowKey as string;
  const keyOf = React.useCallback((record: T) => String((record as Record<string, unknown>)[rowKeyStr]), [rowKeyStr]);

  const { columnDefinitions, allColumnsMap, actionsColumn, storageKey } = React.useMemo(() => {
    const keyed = columns.map((column, index) => ({ column, key: columnKeyOf(column, index) }));
    const data = keyed.filter(({ column }) => column.valueType !== VALUE_TYPE_OPTION);
    const options = keyed.filter(({ column }) => column.valueType === VALUE_TYPE_OPTION);
    return {
      columnDefinitions: data.map(({ key }) => ({ key, defaultVisible: true })) as ColumnDefinition[],
      allColumnsMap: Object.fromEntries(data.map(({ column, key }) => [key, { ...column, key } as ProColumnsWithMeasure<T>])),
      actionsColumn: options.length > 0
        ? {
            key: "__drawerItemActions",
            title: "",
            valueType: "option" as const,
            render: (_: unknown, record: T, index: number) => options.map(({ column, key }) => (
              <React.Fragment key={key}>{(column.render as ((value: unknown, record: T, index: number) => React.ReactNode) | undefined)?.(undefined, record, index)}</React.Fragment>
            )),
          }
        : undefined,
      // Sortierwunsch je Spaltensatz: gleiche Positionslisten teilen ihn, andere bleiben getrennt.
      storageKey: `drawer-items.${data.map(({ key }) => key).join("-")}`,
    };
  }, [columns]);

  const changeSelection = React.useCallback(
    (keys: React.Key[]) => {
      if (!rowSelection) return;
      let nextKeys = keys.map(String);
      if (rowSelection.type === "radio") {
        const previous = new Set((rowSelection.selectedRowKeys ?? []).map(String));
        const added = nextKeys.filter((k) => !previous.has(k));
        nextKeys = added.length > 0 ? [added[added.length - 1]] : nextKeys.slice(0, 1);
      }
      const keySet = new Set(nextKeys);
      rowSelection.onChange(nextKeys, dataSource.filter((r) => keySet.has(keyOf(r))));
    },
    [dataSource, keyOf, rowSelection]
  );

  const toggleRow = React.useCallback(
    (record: T) => {
      if (!rowSelection || rowSelection.getCheckboxProps?.(record)?.disabled) return;
      const key = keyOf(record);
      const current = (rowSelection.selectedRowKeys ?? []).map(String);
      changeSelection(current.includes(key) ? current.filter((k) => k !== key) : [...current, key]);
    },
    [changeSelection, keyOf, rowSelection]
  );

  if (dataSource.length === 0 && emptyText != null) {
    return <>{emptyText}</>;
  }

  const activeRow = isRowActive ? dataSource.find((record) => isRowActive(record)) : undefined;

  return (
    <div style={{ maxHeight: scrollY, overflowY: "auto" }}>
      <SettingsListPage<T>
        storageKey={storageKey}
        dataSource={dataSource}
        rowKey={keyOf}
        columnDefinitions={columnDefinitions}
        allColumnsMap={allColumnsMap}
        actionsColumn={actionsColumn}
        showColumnEditor={false}
        pagination={false}
        embedded
        rowSelection={rowSelection ? {
          selectedRowKeys: rowSelection.selectedRowKeys,
          onChange: (keys) => changeSelection(keys),
          getCheckboxProps: rowSelection.getCheckboxProps,
          hideSelectAll: rowSelection.hideSelectAll || rowSelection.type === "radio",
        } : undefined}
        onRowClickForSelection={rowSelection?.selectionByRowClick ? (record) => toggleRow(record) : undefined}
        onRowClick={!rowSelection && onRowClick ? (record) => { if (!isRowDisabled?.(record)) onRowClick(record); } : undefined}
        activeRowKey={activeRow ? keyOf(activeRow) : undefined}
        emptyText={emptyText}
      />
    </div>
  );
}
